import { useState } from "react";
import { Link } from "react-router-dom";

const NEWSLETTER_URL = "https://readdy.ai/api/form/d7b4n3s9quqsda8uesl0";

const Footer: React.FC = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch(NEWSLETTER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
      setEmail("");
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-stone-900 text-white mt-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="md:col-span-1">
            <img
              src="https://public.readdy.ai/ai/img_res/4137faf5-b286-4eb1-a7fc-7d3899999f96.png"
              alt="STRAY LOFT LLC"
              className="h-12 w-auto object-contain mb-4"
            />
            <p className="text-stone-400 text-sm leading-relaxed mb-4">
              Professional hardware tools for contractors and DIY enthusiasts. Quality you can trust, prices you can afford.
            </p>
            <div className="flex items-center gap-3">
              <a href="#" className="w-8 h-8 flex items-center justify-center bg-stone-800 rounded-lg hover:bg-orange-500 transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-sm"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center bg-stone-800 rounded-lg hover:bg-orange-500 transition-colors cursor-pointer">
                <i className="ri-instagram-line text-sm"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center bg-stone-800 rounded-lg hover:bg-orange-500 transition-colors cursor-pointer">
                <i className="ri-linkedin-fill text-sm"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center bg-stone-800 rounded-lg hover:bg-orange-500 transition-colors cursor-pointer">
                <i className="ri-youtube-fill text-sm"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold text-stone-300 uppercase tracking-wider mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { label: "All Products", to: "/products" },
                { label: "Power Tools", to: "/products?category=power-tools" },
                { label: "Hand Tools", to: "/products?category=hand-tools" },
                { label: "Safety Equipment", to: "/products?category=safety" },
                { label: "Storage Solutions", to: "/products?category=storage" },
              ].map((link) => (
                <li key={link.label}>
                  <Link to={link.to} className="text-stone-400 text-sm hover:text-orange-400 transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-sm font-semibold text-stone-300 uppercase tracking-wider mb-4">Company</h4>
            <ul className="space-y-2">
              {[
                { label: "About Us", to: "/about" },
                { label: "Contact Us", to: "/contact" },
                { label: "Shipping Policy", to: "/contact" },
                { label: "Return Policy", to: "/contact" },
                { label: "Privacy Policy", to: "/contact" },
              ].map((link) => (
                <li key={link.label}>
                  <Link to={link.to} className="text-stone-400 text-sm hover:text-orange-400 transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + Newsletter */}
          <div>
            <h4 className="text-sm font-semibold text-stone-300 uppercase tracking-wider mb-4">Contact Us</h4>
            <div className="space-y-2 mb-6">
              <div className="flex items-start gap-2 text-stone-400 text-sm">
                <i className="ri-map-pin-line mt-0.5 text-orange-400 flex-shrink-0"></i>
                <span>3537 E Yale St, Phoenix, AZ 85008</span>
              </div>
              <div className="flex items-center gap-2 text-stone-400 text-sm">
                <i className="ri-mail-line text-orange-400 flex-shrink-0"></i>
                <a href="mailto:strayloftllc@outlook.com" className="hover:text-orange-400 transition-colors">
                  strayloftllc@outlook.com
                </a>
              </div>
            </div>

            <h4 className="text-sm font-semibold text-stone-300 uppercase tracking-wider mb-3">Newsletter</h4>
            {submitted ? (
              <p className="text-orange-400 text-sm">Thanks for subscribing!</p>
            ) : (
              <form onSubmit={handleSubscribe} data-readdy-form className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="flex-1 px-3 py-2 text-sm bg-stone-800 border border-stone-700 rounded-lg text-white placeholder-stone-500 focus:outline-none focus:border-orange-400"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-3 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {submitting ? "..." : "Join"}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="border-t border-stone-800 mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-stone-500 text-sm">
            &copy; 2025 STRAY LOFT LLC. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-stone-500 text-xs">
              <i className="ri-shield-check-line text-green-500"></i>
              <span>Secure Payments</span>
            </div>
            <div className="flex items-center gap-2 text-stone-500 text-xs">
              <i className="ri-bank-card-line text-blue-400"></i>
              <span>Stripe Protected</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
